import React, { useState, useEffect, useCallback } from 'react'
import {
  Card,
  Button,
  Descriptions,
  Tag,
  Table,
  Tabs,
  Spin,
  message,
  Empty,
  Statistic,
  Row,
  Col,
  Pagination
} from 'antd'
import {
  ArrowLeftOutlined,
  EditOutlined,
  TeamOutlined,
  WalletOutlined,
  ShoppingOutlined,
  DollarOutlined
} from '@ant-design/icons'
import styled from 'styled-components'
import { useMediaQuery } from 'react-responsive'
import { useAuth } from '../hooks/AuthContext'
import useGoBack from '../hooks/useGoBack'
import { useNavigate, useParams } from 'react-router-dom'
import api from '../utils/api'
import dayjs from 'dayjs'
import { formatTz } from '../utils/timezone'

const PageContainer = styled.div`
  padding: 24px;
  background: #f5f5f5;
  min-height: 100vh;

  @media (max-width: 768px) {
    padding: 12px;
  }
`

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 12px;
  }
`

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 768px) {
    gap: 10px;
  }
`

const StyledCard = styled(Card)`
  border-radius: 12px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  .ant-card-body {
    padding: 24px;
  }

  @media (max-width: 768px) {
    margin-bottom: 12px;
    border-radius: 8px;

    .ant-card-body {
      padding: 12px 14px;
    }
    .ant-card-head {
      padding: 8px 14px;
      min-height: 40px;
    }
    .ant-card-head-title {
      font-size: 15px;
    }
    .ant-tabs-nav {
      margin-bottom: 12px;
    }
    .ant-tabs-tab {
      padding: 6px 10px;
      font-size: 13px;
    }
    .ant-tabs-content-holder {
      padding: 0;
    }
  }
`

/* 手機版明細卡片：緊湊、小間距 */
const MobileCardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 2px 0;
`

const MobileDetailCard = styled.div`
  background: #fafafa;
  border: 1px solid #eee;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 13px;
  line-height: 1.3;
`

const MobileCardRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 2px;

  &:last-child {
    margin-bottom: 0;
  }
`

const MobileCardLabel = styled.span`
  color: #666;
  flex-shrink: 0;
`

const MobileCardValue = styled.span`
  color: #1a1a1a;
  font-weight: 500;
  text-align: right;
  word-break: break-all;
`

const RELATIONSHIP_LABELS = { family: '家庭', company: '公司', friends: '朋友', other: '其他' }

const formatCurrency = (v) => {
  const n = Number(v)
  if (Number.isNaN(n)) return '-'
  return `NT$ ${n.toLocaleString()}`
}

const MOBILE_PAGE_SIZE = 10

const CustomerGroupDetailPage = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const goBack = useGoBack('/customers/groups')
  const { groupId } = useParams()
  const isMobile = useMediaQuery({ maxWidth: 768 })
  const [loading, setLoading] = useState(true)
  const [txLoading, setTxLoading] = useState(false)
  const [group, setGroup] = useState(null)
  const [txData, setTxData] = useState(null)
  const [activeTab, setActiveTab] = useState('overview')
  const [mobilePage, setMobilePage] = useState(1)

  const businessId = user?.businessId

  useEffect(() => {
    setMobilePage(1)
  }, [activeTab])

  const fetchGroup = useCallback(async () => {
    if (!businessId || !groupId) return
    setLoading(true)
    try {
      const res = await api.get(
        `/api/business_B/${businessId}/customers/groups/${groupId}`
      )
      setGroup(res.data?.data ?? res.data ?? null)
    } catch (err) {
      console.error('取得群組失敗:', err)
      message.error(err?.response?.data?.message || '取得群組失敗')
      navigate('/customers/groups')
    } finally {
      setLoading(false)
    }
  }, [businessId, groupId, navigate])

  const fetchTransactions = useCallback(async () => {
    if (!businessId || !groupId) return
    setTxLoading(true)
    try {
      const res = await api.get(
        `/api/business_B/${businessId}/customers/groups/${groupId}/transactions`
      )
      setTxData(res.data?.data ?? res.data ?? null)
    } catch (err) {
      console.error('取得群組交易資料失敗:', err)
      message.error(err?.response?.data?.message || '取得交易資料失敗')
      setTxData(null)
    } finally {
      setTxLoading(false)
    }
  }, [businessId, groupId])

  useEffect(() => {
    fetchGroup()
  }, [fetchGroup])

  useEffect(() => {
    if (groupId && !loading) fetchTransactions()
  }, [groupId, loading, fetchTransactions])

  if (loading || !group) {
    return (
      <PageContainer>
        <div style={{ textAlign: 'center', padding: isMobile ? 40 : 80 }}>
          <Spin size='large' tip='載入中...' />
        </div>
      </PageContainer>
    )
  }

  const members = group.members ?? []
  const summary = txData?.summary ?? {}
  const balances = txData?.balances ?? []
  const transactions = txData?.transactions ?? []
  const orders = txData?.orders ?? []
  const purchases = txData?.purchases ?? []
  const bookings = txData?.bookings ?? []
  const businessName = txData?.businessName

  // 新 API：type 為「扣除」「存入」等中文；餘額用 balanceAfter；含 deductAmount、orderId、orderReceivable（相容舊 balance）
  const txColumns = [
    { title: '顧客', dataIndex: 'customerName', key: 'customerName', width: 120 },
    { title: '類型', dataIndex: 'type', key: 'type', width: 80, render: (t) => (t === 'topup' ? '儲值/轉入' : t === 'deduct' ? '扣抵/轉出' : t || '-') },
    { title: '金額', dataIndex: 'amount', key: 'amount', width: 100, align: 'right', render: (a) => formatCurrency(a) },
    { title: '扣款儲值金', dataIndex: 'deductAmount', key: 'deductAmount', width: 100, align: 'right', render: (a) => (a != null ? formatCurrency(a) : '-') },
    { title: '交易後餘額', dataIndex: 'balanceAfter', key: 'balanceAfter', width: 110, align: 'right', render: (b, r) => (b != null || r?.balance != null ? formatCurrency(b ?? r.balance) : '-') },
    { title: '訂單編號', dataIndex: 'orderId', key: 'orderId', width: 100, ellipsis: true, render: (v) => v || '-' },
    { title: '訂單應收', dataIndex: 'orderReceivable', key: 'orderReceivable', width: 100, align: 'right', render: (a) => (a != null ? formatCurrency(a) : '-') },
    { title: '備註', dataIndex: 'note', key: 'note', ellipsis: true },
    { title: '時間', dataIndex: 'createdAt', key: 'createdAt', width: 160, render: (d) => formatTz(d, 'YYYY-MM-DD HH:mm') }
  ]

  // 新 API：receivable / paid / unpaid；payment 為 { storedValue, cash, transfer }；舊 API 為 paymentMethod 字串
  const formatPayment = (p) => {
    if (p == null) return '-'
    if (typeof p === 'string') return p
    if (typeof p !== 'object') return '-'
    const parts = []
    if (Number(p.storedValue) > 0) parts.push(`儲值 ${formatCurrency(p.storedValue)}`)
    if (Number(p.cash) > 0) parts.push(`現金 ${formatCurrency(p.cash)}`)
    if (Number(p.transfer) > 0) parts.push(`轉帳 ${formatCurrency(p.transfer)}`)
    return parts.length ? parts.join('、') : '-'
  }
  const orderColumns = [
    { title: '訂單編號', dataIndex: 'orderId', key: 'orderId', width: 110, ellipsis: true, render: (v) => v || '-' },
    { title: '顧客', dataIndex: 'customerName', key: 'customerName', width: 100 },
    { title: '應收', dataIndex: 'receivable', key: 'receivable', width: 90, align: 'right', render: (a, r) => formatCurrency(a ?? r?.totalAmount ?? 0) },
    { title: '已付', dataIndex: 'paid', key: 'paid', width: 90, align: 'right', render: (a) => formatCurrency(a ?? 0) },
    { title: '未付', dataIndex: 'unpaid', key: 'unpaid', width: 90, align: 'right', render: (a) => formatCurrency(a ?? 0) },
    { title: '狀態', dataIndex: 'status', key: 'status', width: 80, render: (s) => (s ? <Tag>{s}</Tag> : '-') },
    { title: '付款組成', dataIndex: 'payment', key: 'payment', width: 140, ellipsis: true, render: (p, r) => formatPayment(p ?? r?.paymentMethod) },
    { title: '時間', dataIndex: 'createdAt', key: 'createdAt', width: 160, render: (d) => formatTz(d, 'YYYY-MM-DD HH:mm') }
  ]

  // 新 API：valueAddedAmount、bonusAmount、totalPayment（valueAddedAmount 相容舊 API 無時用 totalPayment）
  const purchaseColumns = [
    { title: '顧客', dataIndex: 'customerName', key: 'customerName', width: 120 },
    { title: '儲值金額', dataIndex: 'valueAddedAmount', key: 'valueAddedAmount', width: 100, align: 'right', render: (a, r) => formatCurrency(a ?? r?.totalPayment ?? 0) },
    { title: '贈送', dataIndex: 'bonusAmount', key: 'bonusAmount', width: 90, align: 'right', render: (a) => formatCurrency(a ?? 0) },
    { title: '總付款', dataIndex: 'totalPayment', key: 'totalPayment', width: 100, align: 'right', render: (a) => formatCurrency(a ?? 0) },
    { title: '時間', dataIndex: 'createdAt', key: 'createdAt', width: 160, render: (d) => formatTz(d, 'YYYY-MM-DD HH:mm') }
  ]

  // 新 API：date + time；productName、services；舊 API 為 appointmentDate
  const bookingColumns = [
    { title: '顧客', dataIndex: 'customerName', key: 'customerName', width: 100 },
    { title: '預約時間', key: 'datetime', width: 160, render: (_, r) => (r.date && r.time ? `${r.date} ${r.time}` : r.appointmentDate ? dayjs(r.appointmentDate).format('YYYY-MM-DD HH:mm') : '-') },
    { title: '服務', dataIndex: 'productName', key: 'productName', width: 120, render: (name, r) => (name || (Array.isArray(r.services) && r.services[0]) || '-') },
    { title: '狀態', dataIndex: 'status', key: 'status', width: 90, render: (s) => (s ? <Tag>{s}</Tag> : '-') }
  ]

  const renderOverview = () => (
    <>
      <StyledCard title='群組資訊'>
        {isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: '#666' }}>群組名稱</span><span>{group.name}</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: '#666' }}>描述</span><span>{group.description || '-'}</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><span style={{ color: '#666' }}>關係類型</span><Tag color='blue' style={{ margin: 0 }}>{RELATIONSHIP_LABELS[group.relationship] ?? group.relationship ?? '其他'}</Tag></div>
            <div><span style={{ color: '#666', marginRight: 6 }}>成員</span>{members.map((m) => (<Tag key={m._id ?? m.id} style={{ margin: '2px 4px 2px 0' }}>{m.name ?? m.userInfo?.name ?? '未命名'}</Tag>))}</div>
            {group.notes && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: '#666' }}>備註</span><span>{group.notes}</span></div>}
          </div>
        ) : (
          <Descriptions column={1} bordered size='small'>
            <Descriptions.Item label='群組名稱'>{group.name}</Descriptions.Item>
            <Descriptions.Item label='描述'>{group.description || '-'}</Descriptions.Item>
            <Descriptions.Item label='關係類型'><Tag color='blue'>{RELATIONSHIP_LABELS[group.relationship] ?? group.relationship ?? '其他'}</Tag></Descriptions.Item>
            <Descriptions.Item label='成員'>{members.map((m) => (<Tag key={m._id ?? m.id}>{m.name ?? m.userInfo?.name ?? '未命名'}</Tag>))}</Descriptions.Item>
            {group.notes && <Descriptions.Item label='備註'>{group.notes}</Descriptions.Item>}
          </Descriptions>
        )}
      </StyledCard>
      {txData && (
        <StyledCard title={businessName ? `統計總計 · ${businessName}` : '統計總計'}>
          <Row gutter={isMobile ? 8 : 24}>
            <Col xs={12} sm={6}>
              <Statistic title='總餘額' value={summary.totalBalance ?? 0} formatter={(v) => formatCurrency(v)} prefix={!isMobile && <WalletOutlined />} valueStyle={{ fontSize: isMobile ? 14 : undefined }} titleStyle={isMobile ? { fontSize: 12 } : undefined} />
            </Col>
            <Col xs={12} sm={6}>
              <Statistic title='總訂單金額' value={summary.totalOrders ?? 0} formatter={(v) => formatCurrency(v)} prefix={!isMobile && <ShoppingOutlined />} valueStyle={{ fontSize: isMobile ? 14 : undefined }} titleStyle={isMobile ? { fontSize: 12 } : undefined} />
            </Col>
            <Col xs={12} sm={6}>
              <Statistic title='總儲值金額' value={summary.totalPurchases ?? 0} formatter={(v) => formatCurrency(v)} prefix={!isMobile && <DollarOutlined />} valueStyle={{ fontSize: isMobile ? 14 : undefined }} titleStyle={isMobile ? { fontSize: 12 } : undefined} />
            </Col>
            <Col xs={12} sm={6}>
              <Statistic title='成員數' value={summary.memberCount ?? members.length} prefix={!isMobile && <TeamOutlined />} valueStyle={{ fontSize: isMobile ? 14 : undefined }} titleStyle={isMobile ? { fontSize: 12 } : undefined} />
            </Col>
          </Row>
          {(summary.orderCount != null || summary.purchaseCount != null || summary.bookingCount != null || summary.transactionCount != null) && (
            <Row gutter={isMobile ? 8 : 24} style={{ marginTop: isMobile ? 8 : 16 }}>
              {summary.transactionCount != null && <Col span={isMobile ? 12 : 6}><Statistic title='交易筆數' value={summary.transactionCount} valueStyle={{ fontSize: isMobile ? 13 : undefined }} titleStyle={isMobile ? { fontSize: 12 } : undefined} /></Col>}
              {summary.orderCount != null && <Col span={isMobile ? 12 : 6}><Statistic title='訂單筆數' value={summary.orderCount} valueStyle={{ fontSize: isMobile ? 13 : undefined }} titleStyle={isMobile ? { fontSize: 12 } : undefined} /></Col>}
              {summary.purchaseCount != null && <Col span={isMobile ? 12 : 6}><Statistic title='儲值筆數' value={summary.purchaseCount} valueStyle={{ fontSize: isMobile ? 13 : undefined }} titleStyle={isMobile ? { fontSize: 12 } : undefined} /></Col>}
              {summary.bookingCount != null && <Col span={isMobile ? 12 : 6}><Statistic title='預約筆數' value={summary.bookingCount} valueStyle={{ fontSize: isMobile ? 13 : undefined }} titleStyle={isMobile ? { fontSize: 12 } : undefined} /></Col>}
            </Row>
          )}
          {balances.length > 0 && (
            <div style={{ marginTop: isMobile ? 10 : 16 }}>
              <div style={{ marginBottom: isMobile ? 4 : 8, fontWeight: 500, fontSize: isMobile ? 13 : undefined }}>各成員餘額</div>
              {balances.map((b) => (
                <div key={b.customerId} style={{ marginBottom: isMobile ? 2 : 4, fontSize: isMobile ? 13 : undefined }}>
                  {b.customerName}: {formatCurrency(b.balance)}
                  {(b.totalTopup != null || b.totalDeposited != null) && `（累計儲值 ${formatCurrency(b.totalTopup ?? b.totalDeposited)}）`}
                </div>
              ))}
            </div>
          )}
        </StyledCard>
      )}
    </>
  )

  const tabItems = [
    {
      key: 'overview',
      label: '總覽',
      children: renderOverview()
    },
    {
      key: 'transactions',
      label: '交易記錄',
      children: (
        <Spin spinning={txLoading}>
          {isMobile ? (
            <>
              {transactions.length === 0 ? (
                <Empty description='尚無交易記錄' image={Empty.PRESENTED_IMAGE_SIMPLE} />
              ) : (
                <>
                  <MobileCardList>
                    {transactions
                      .slice((mobilePage - 1) * MOBILE_PAGE_SIZE, mobilePage * MOBILE_PAGE_SIZE)
                      .map((r, i) => (
                        <MobileDetailCard key={r.id ?? r._id ?? `${r.customerId}-${r.createdAt || ''}-${i}`}>
                          <MobileCardRow><MobileCardLabel>顧客</MobileCardLabel><MobileCardValue>{r.customerName || '-'}</MobileCardValue></MobileCardRow>
                          <MobileCardRow><MobileCardLabel>類型</MobileCardLabel><MobileCardValue>{r.type === 'topup' ? '儲值/轉入' : r.type === 'deduct' ? '扣抵/轉出' : r.type || '-'}</MobileCardValue></MobileCardRow>
                          <MobileCardRow><MobileCardLabel>金額</MobileCardLabel><MobileCardValue>{formatCurrency(r.amount)}</MobileCardValue></MobileCardRow>
                          <MobileCardRow><MobileCardLabel>餘額</MobileCardLabel><MobileCardValue>{formatCurrency(r.balanceAfter ?? r.balance)}</MobileCardValue></MobileCardRow>
                          {r.note && <MobileCardRow><MobileCardLabel>備註</MobileCardLabel><MobileCardValue style={{ fontWeight: 400 }}>{r.note}</MobileCardValue></MobileCardRow>}
                          <MobileCardRow><MobileCardLabel>時間</MobileCardLabel><MobileCardValue style={{ fontWeight: 400, fontSize: 12 }}>{formatTz(r.createdAt, 'MM/DD HH:mm')}</MobileCardValue></MobileCardRow>
                        </MobileDetailCard>
                      ))}
                  </MobileCardList>
                  {transactions.length > MOBILE_PAGE_SIZE && (
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8, paddingTop: 8, borderTop: '1px solid #eee' }}>
                      <Pagination current={mobilePage} pageSize={MOBILE_PAGE_SIZE} total={transactions.length} onChange={setMobilePage} size='small' simple showSizeChanger={false} />
                    </div>
                  )}
                </>
              )}
            </>
          ) : (
            <Table
              rowKey={(r, i) => r.id ?? r._id ?? `${r.customerId}-${r.createdAt || ''}-${i}`}
              columns={txColumns}
              dataSource={transactions}
              pagination={{ pageSize: 10, showTotal: (t) => `共 ${t} 筆` }}
              scroll={{ x: 1000 }}
              locale={{ emptyText: <Empty description='尚無交易記錄' /> }}
            />
          )}
        </Spin>
      )
    },
    {
      key: 'orders',
      label: '訂單',
      children: (
        <Spin spinning={txLoading}>
          {isMobile ? (
            <>
              {orders.length === 0 ? (
                <Empty description='尚無訂單' image={Empty.PRESENTED_IMAGE_SIMPLE} />
              ) : (
                <>
                  <MobileCardList>
                    {orders.slice((mobilePage - 1) * MOBILE_PAGE_SIZE, mobilePage * MOBILE_PAGE_SIZE).map((r) => (
                      <MobileDetailCard key={r.id ?? r._id ?? r.orderId}>
                        <MobileCardRow><MobileCardLabel>訂單</MobileCardLabel><MobileCardValue style={{ fontSize: 12 }}>{r.orderId || '-'}</MobileCardValue></MobileCardRow>
                        <MobileCardRow><MobileCardLabel>顧客</MobileCardLabel><MobileCardValue>{r.customerName || '-'}</MobileCardValue></MobileCardRow>
                        <MobileCardRow><MobileCardLabel>應收</MobileCardLabel><MobileCardValue>{formatCurrency(r.receivable ?? r.totalAmount ?? 0)}</MobileCardValue></MobileCardRow>
                        <MobileCardRow><MobileCardLabel>狀態</MobileCardLabel><MobileCardValue><Tag style={{ margin: 0 }}>{r.status || '-'}</Tag></MobileCardValue></MobileCardRow>
                        <MobileCardRow><MobileCardLabel>時間</MobileCardLabel><MobileCardValue style={{ fontWeight: 400, fontSize: 12 }}>{formatTz(r.createdAt, 'MM/DD HH:mm')}</MobileCardValue></MobileCardRow>
                      </MobileDetailCard>
                    ))}
                  </MobileCardList>
                  {orders.length > MOBILE_PAGE_SIZE && (
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8, paddingTop: 8, borderTop: '1px solid #eee' }}>
                      <Pagination current={mobilePage} pageSize={MOBILE_PAGE_SIZE} total={orders.length} onChange={setMobilePage} size='small' simple showSizeChanger={false} />
                    </div>
                  )}
                </>
              )}
            </>
          ) : (
            <Table
              rowKey={(r) => r.id ?? r._id ?? Math.random()}
              columns={orderColumns}
              dataSource={orders}
              pagination={{ pageSize: 10, showTotal: (t) => `共 ${t} 筆` }}
              locale={{ emptyText: <Empty description='尚無訂單' /> }}
            />
          )}
        </Spin>
      )
    },
    {
      key: 'purchases',
      label: '儲值',
      children: (
        <Spin spinning={txLoading}>
          {isMobile ? (
            <>
              {purchases.length === 0 ? (
                <Empty description='尚無儲值記錄' image={Empty.PRESENTED_IMAGE_SIMPLE} />
              ) : (
                <>
                  <MobileCardList>
                    {purchases.slice((mobilePage - 1) * MOBILE_PAGE_SIZE, mobilePage * MOBILE_PAGE_SIZE).map((r) => (
                      <MobileDetailCard key={r.id ?? r._id}>
                        <MobileCardRow><MobileCardLabel>顧客</MobileCardLabel><MobileCardValue>{r.customerName || '-'}</MobileCardValue></MobileCardRow>
                        <MobileCardRow><MobileCardLabel>儲值</MobileCardLabel><MobileCardValue>{formatCurrency(r.valueAddedAmount ?? r.totalPayment ?? 0)}</MobileCardValue></MobileCardRow>
                        <MobileCardRow><MobileCardLabel>贈送</MobileCardLabel><MobileCardValue>{formatCurrency(r.bonusAmount ?? 0)}</MobileCardValue></MobileCardRow>
                        <MobileCardRow><MobileCardLabel>時間</MobileCardLabel><MobileCardValue style={{ fontWeight: 400, fontSize: 12 }}>{formatTz(r.createdAt, 'MM/DD HH:mm')}</MobileCardValue></MobileCardRow>
                      </MobileDetailCard>
                    ))}
                  </MobileCardList>
                  {purchases.length > MOBILE_PAGE_SIZE && (
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8, paddingTop: 8, borderTop: '1px solid #eee' }}>
                      <Pagination current={mobilePage} pageSize={MOBILE_PAGE_SIZE} total={purchases.length} onChange={setMobilePage} size='small' simple showSizeChanger={false} />
                    </div>
                  )}
                </>
              )}
            </>
          ) : (
            <Table
              rowKey={(r) => r.id ?? r._id ?? Math.random()}
              columns={purchaseColumns}
              dataSource={purchases}
              pagination={{ pageSize: 10, showTotal: (t) => `共 ${t} 筆` }}
              locale={{ emptyText: <Empty description='尚無儲值記錄' /> }}
            />
          )}
        </Spin>
      )
    },
    {
      key: 'bookings',
      label: '預約',
      children: (
        <Spin spinning={txLoading}>
          {isMobile ? (
            <>
              {bookings.length === 0 ? (
                <Empty description='尚無預約' image={Empty.PRESENTED_IMAGE_SIMPLE} />
              ) : (
                <>
                  <MobileCardList>
                    {bookings.slice((mobilePage - 1) * MOBILE_PAGE_SIZE, mobilePage * MOBILE_PAGE_SIZE).map((r) => (
                      <MobileDetailCard key={r.id ?? r._id}>
                        <MobileCardRow><MobileCardLabel>顧客</MobileCardLabel><MobileCardValue>{r.customerName || '-'}</MobileCardValue></MobileCardRow>
                        <MobileCardRow><MobileCardLabel>時間</MobileCardLabel><MobileCardValue>{r.date && r.time ? `${r.date} ${r.time}` : r.appointmentDate ? dayjs(r.appointmentDate).format('MM/DD HH:mm') : '-'}</MobileCardValue></MobileCardRow>
                        <MobileCardRow><MobileCardLabel>服務</MobileCardLabel><MobileCardValue style={{ fontWeight: 400 }}>{r.productName || (Array.isArray(r.services) && r.services[0]) || '-'}</MobileCardValue></MobileCardRow>
                        <MobileCardRow><MobileCardLabel>狀態</MobileCardLabel><MobileCardValue><Tag style={{ margin: 0 }}>{r.status || '-'}</Tag></MobileCardValue></MobileCardRow>
                      </MobileDetailCard>
                    ))}
                  </MobileCardList>
                  {bookings.length > MOBILE_PAGE_SIZE && (
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8, paddingTop: 8, borderTop: '1px solid #eee' }}>
                      <Pagination current={mobilePage} pageSize={MOBILE_PAGE_SIZE} total={bookings.length} onChange={setMobilePage} size='small' simple showSizeChanger={false} />
                    </div>
                  )}
                </>
              )}
            </>
          ) : (
            <Table
              rowKey={(r) => r.id ?? r._id ?? Math.random()}
              columns={bookingColumns}
              dataSource={bookings}
              pagination={{ pageSize: 10, showTotal: (t) => `共 ${t} 筆` }}
              locale={{ emptyText: <Empty description='尚無預約' /> }}
            />
          )}
        </Spin>
      )
    }
  ]

  return (
    <PageContainer>
      <PageHeader>
        <HeaderLeft>
          <Button size={isMobile ? 'small' : 'middle'} icon={<ArrowLeftOutlined />} onClick={goBack}>
            返回
          </Button>
          <span style={{ fontSize: isMobile ? 17 : 20, fontWeight: 600 }}>{group.name}</span>
          {group.isActive === false && <Tag color='default'>已停用</Tag>}
        </HeaderLeft>
        <Button
          type='primary'
          size={isMobile ? 'small' : 'middle'}
          icon={<EditOutlined />}
          onClick={() => navigate(`/customers/groups/${groupId}/edit`)}
        >
          編輯群組
        </Button>
      </PageHeader>

      <StyledCard bodyStyle={isMobile ? { padding: '10px 14px' } : undefined}>
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
      </StyledCard>
    </PageContainer>
  )
}

export default CustomerGroupDetailPage
